import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://yliu.ca';
const DEFAULT_IMAGE = `${SITE_URL}/yliulogo.png`;
const DEFAULT_TITLE = 'Y. Liu Law Firm | Cross-Border Legal Services in Toronto and Beijing';
const DEFAULT_DESCRIPTION = 'Y. Liu Law Firm provides bilingual cross-border legal services in Toronto and Beijing, including business law, civil litigation, family law, and real estate law.';

type RouteSeoConfig = {
  title: string;
  description: string;
  canonicalPath: string;
  robots?: string;
};

const ROUTE_CONFIG: Record<string, RouteSeoConfig> = {
  '/': {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    canonicalPath: '/',
    robots: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
  },
  '/articles': {
    title: 'Legal Articles and Insights | Y. Liu Law Firm',
    description: 'Read legal articles and practical insights from Y. Liu Law Firm on cross-border legal matters, business law, civil disputes, family law, and real estate issues.',
    canonicalPath: '/articles',
    robots: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
  },
  '/contact': {
    title: 'Contact Y. Liu Law Firm | Toronto and Beijing Offices',
    description: 'Contact Y. Liu Law Firm in Toronto or Beijing to discuss cross-border legal services, consultations, litigation, business law, family law, and real estate law.',
    canonicalPath: '/contact',
    robots: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
  },
  '/intake': {
    title: 'Client Intake Form | Y. Liu Law Firm',
    description: 'Internal client intake form for Y. Liu Law Firm.',
    canonicalPath: '/intake',
    robots: 'noindex, nofollow, noarchive, nosnippet'
  },
  '/login': {
    title: 'Client Login | Y. Liu Law Firm',
    description: 'Secure login for Y. Liu Law Firm clients, lawyers, and administrators.',
    canonicalPath: '/login',
    robots: 'noindex, nofollow'
  }
};

const upsertMeta = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
};

const upsertLink = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector(selector) as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement('link');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
};

const upsertJsonLd = (id: string, content: Record<string, unknown>) => {
  let element = document.getElementById(id) as HTMLScriptElement | null;

  if (!element) {
    element = document.createElement('script');
    element.id = id;
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(content);
};

const getRouteConfig = (pathname: string): RouteSeoConfig => {
  if (ROUTE_CONFIG[pathname]) {
    return ROUTE_CONFIG[pathname];
  }

  if (
    pathname.startsWith('/admin') ||
    pathname.startsWith('/lawyer') ||
    pathname.startsWith('/client') ||
    pathname.startsWith('/profile')
  ) {
    return {
      title: 'Y. Liu Law Firm Portal',
      description: 'Secure portal for Y. Liu Law Firm clients, lawyers, and administrators.',
      canonicalPath: pathname,
      robots: 'noindex, nofollow'
    };
  }

  return {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    canonicalPath: pathname,
    robots: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
  };
};

const RouteSeo: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const config = getRouteConfig(location.pathname);
    const canonicalUrl = `${SITE_URL}${config.canonicalPath}`;

    document.title = config.title;

    upsertMeta('meta[name="description"]', {
      name: 'description',
      content: config.description
    });
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: config.robots || 'index, follow'
    });
    upsertMeta('meta[property="og:type"]', {
      property: 'og:type',
      content: 'website'
    });
    upsertMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: config.title
    });
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: config.description
    });
    upsertMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: canonicalUrl
    });
    upsertMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: DEFAULT_IMAGE
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: 'twitter:title',
      content: config.title
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: config.description
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: 'twitter:image',
      content: DEFAULT_IMAGE
    });

    upsertLink('link[rel="canonical"]', {
      rel: 'canonical',
      href: canonicalUrl
    });

    upsertJsonLd('route-seo-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: config.title,
      description: config.description,
      url: canonicalUrl,
      isPartOf: {
        '@id': `${SITE_URL}/#website`
      },
      about: {
        '@id': `${SITE_URL}/#legalservice`
      }
    });
  }, [location.pathname]);

  return null;
};

export default RouteSeo;